import { token } from '@/infrastructure/container';
import {
  type BomEntry,
  type CanonicalBundle,
  type EpochDay,
  type Part,
  type PurchaseRecommendation,
  type RecommendationConfidence,
  type Settings,
  type SimulationResult,
  type SkuId,
  type StockoutEvent,
} from '@/domain/beforered';

export interface IBomCompute {
  simulate(bundle: CanonicalBundle, settings: Settings): SimulationResult;
}

export const TOKEN_IBomCompute = token<IBomCompute>('IBomCompute');

interface ChildEdge {
  child: SkuId;
  qtyPerParent: number;
}

type DirectBom = Map<SkuId, ChildEdge[]>;
type PerDayMap = Map<SkuId, Map<EpochDay, number>>;

const addAt = (m: PerDayMap, sku: SkuId, day: EpochDay, qty: number): void => {
  if (qty <= 0) return;
  let perSku = m.get(sku);
  if (!perSku) {
    perSku = new Map();
    m.set(sku, perSku);
  }
  perSku.set(day, (perSku.get(day) ?? 0) + qty);
};

const buildDirectBom = (entries: readonly BomEntry[]): DirectBom => {
  const bom: DirectBom = new Map();
  for (const e of entries) {
    if (e.parent === e.child) continue;
    let kids = bom.get(e.parent);
    if (!kids) {
      kids = [];
      bom.set(e.parent, kids);
    }
    kids.push({ child: e.child, qtyPerParent: e.quantityPerParent });
  }
  return bom;
};

const collectSkus = (bundle: CanonicalBundle): Set<SkuId> => {
  const skus = new Set<SkuId>();
  for (const e of bundle.bom) {
    skus.add(e.parent);
    skus.add(e.child);
  }
  for (const r of bundle.inventory) skus.add(r.sku);
  for (const r of bundle.salesOrders) skus.add(r.sku);
  for (const r of bundle.manufacturingOrders) skus.add(r.sku);
  for (const r of bundle.purchaseOrders) skus.add(r.sku);
  for (const id of bundle.parts.keys()) skus.add(id);
  return skus;
};

const topoOrder = (skus: Set<SkuId>, bom: DirectBom): SkuId[] => {
  const inDegree = new Map<SkuId, number>();
  for (const s of skus) inDegree.set(s, 0);
  for (const [, kids] of bom) {
    for (const k of kids) inDegree.set(k.child, (inDegree.get(k.child) ?? 0) + 1);
  }
  const queue: SkuId[] = [];
  for (const [s, d] of inDegree) if (d === 0) queue.push(s);
  const order: SkuId[] = [];
  const seen = new Set<SkuId>();
  let head = 0;
  while (head < queue.length) {
    const s = queue[head++];
    order.push(s);
    seen.add(s);
    const kids = bom.get(s);
    if (!kids) continue;
    for (const k of kids) {
      const next = (inDegree.get(k.child) ?? 0) - 1;
      inDegree.set(k.child, next);
      if (next === 0) queue.push(k.child);
    }
  }
  for (const s of skus) if (!seen.has(s)) order.push(s);
  return order;
};

const productionLead = (
  parts: ReadonlyMap<SkuId, Part>,
  sku: SkuId,
  settings: Settings,
): number => {
  const p = parts.get(sku);
  return p?.productionLeadTimeDays ?? settings.defaultProductionLeadDays;
};

const purchaseLead = (parts: ReadonlyMap<SkuId, Part>, sku: SkuId, settings: Settings): number => {
  const p = parts.get(sku);
  return p?.purchaseLeadTimeDays ?? settings.defaultPurchaseLeadDays;
};

const partDescription = (parts: ReadonlyMap<SkuId, Part>, sku: SkuId): string =>
  parts.get(sku)?.description ?? sku;

const partUom = (parts: ReadonlyMap<SkuId, Part>, sku: SkuId): string =>
  parts.get(sku)?.uom ?? 'EA';

export class BomCompute implements IBomCompute {
  simulate(bundle: CanonicalBundle, settings: Settings): SimulationResult {
    const bom = buildDirectBom(bundle.bom);
    const skus = collectSkus(bundle);
    const order = topoOrder(skus, bom);

    const onHandIndex = new Map<SkuId, number>();
    for (const inv of bundle.inventory) {
      onHandIndex.set(inv.sku, (onHandIndex.get(inv.sku) ?? 0) + inv.onHand);
    }

    const demand: PerDayMap = new Map();
    const supply: PerDayMap = new Map();
    const driving = new Map<SkuId, Set<string>>();

    const addDrivers = (sku: SkuId, sources: readonly string[]): void => {
      if (sources.length === 0) return;
      let set = driving.get(sku);
      if (!set) {
        set = new Set();
        driving.set(sku, set);
      }
      for (const s of sources) set.add(s);
    };

    // Pre-today demand can't be acted on; compact the past-spilled portion onto the truncated window.
    const smearChildren = (
      children: readonly ChildEdge[],
      L: number,
      completionDay: EpochDay,
      parentQty: number,
      drivers: readonly string[],
    ): void => {
      const completion = Math.max(completionDay, settings.today + 1);
      const windowStart = Math.max(completion - L, settings.today);
      const truncatedDays = Math.max(1, completion - windowStart);
      for (const { child, qtyPerParent } of children) {
        const totalChildQty = parentQty * qtyPerParent;
        const perDay = totalChildQty / truncatedDays;
        for (let d = windowStart; d < windowStart + truncatedDays; d++) {
          addAt(demand, child, d, perDay);
          addDrivers(child, drivers);
        }
      }
    };

    for (const so of bundle.salesOrders) {
      addAt(demand, so.sku, so.scheduledShipDate, so.quantity);
      addDrivers(so.sku, [so.orderNumber]);
    }

    // MO = supply of produced SKU + smeared demand on inputs. Not demand on produced SKU; B3 double-counts otherwise.
    for (const mo of bundle.manufacturingOrders) {
      addAt(supply, mo.sku, mo.scheduledCompletionDate, mo.quantity);
      addDrivers(mo.sku, [mo.workOrderNumber]);
      const kids = bom.get(mo.sku);
      if (kids && kids.length > 0) {
        const L = productionLead(bundle.parts, mo.sku, settings);
        smearChildren(kids, L, mo.scheduledCompletionDate, mo.quantity, [mo.workOrderNumber]);
      }
    }

    for (const po of bundle.purchaseOrders) {
      addAt(supply, po.sku, po.expectedDeliveryDate, po.quantityOutstanding);
    }

    const recommendations: PurchaseRecommendation[] = [];
    const stockoutEvents: StockoutEvent[] = [];

    const horizonEnd = settings.today + settings.horizonDays;
    const baseConfidence: RecommendationConfidence = 'high';

    for (const sku of order) {
      const kids = bom.get(sku);
      const isManufactured = kids !== undefined && kids.length > 0;
      const L = isManufactured
        ? productionLead(bundle.parts, sku, settings)
        : purchaseLead(bundle.parts, sku, settings);

      let projOnHand = onHandIndex.get(sku) ?? 0;
      const skuDemand = demand.get(sku) ?? new Map<EpochDay, number>();
      const skuSupply = supply.get(sku) ?? new Map<EpochDay, number>();

      let firstStockoutDay: EpochDay | null = null;
      let totalDeficit = 0;

      for (let day = settings.today; day < horizonEnd; day++) {
        projOnHand += skuSupply.get(day) ?? 0;

        const gross = skuDemand.get(day) ?? 0;
        if (gross <= 0) continue;

        if (gross <= projOnHand) {
          projOnHand -= gross;
          continue;
        }

        const deficit = gross - projOnHand;
        const drawn = projOnHand;
        projOnHand = 0;

        if (firstStockoutDay === null) firstStockoutDay = day;
        totalDeficit += deficit;
        stockoutEvents.push({ sku, day, gross, drawn, deficit });

        if (isManufactured && kids) {
          const driversArr = Array.from(driving.get(sku) ?? []);
          smearChildren(kids, L, day, deficit, driversArr);
        }
      }

      if (!isManufactured && totalDeficit > 0 && firstStockoutDay !== null) {
        const safetyExtra = totalDeficit * settings.safetyStockPercent;
        const drivingArr = Array.from(driving.get(sku) ?? []).sort();
        recommendations.push({
          sku,
          description: partDescription(bundle.parts, sku),
          suggestedQuantity: totalDeficit + safetyExtra,
          uom: partUom(bundle.parts, sku),
          orderByDate: firstStockoutDay - L,
          neededByDate: firstStockoutDay,
          currentOnHand: onHandIndex.get(sku) ?? 0,
          projectedStockoutDate: firstStockoutDay,
          drivingOrders: drivingArr,
          confidence: baseConfidence,
        });
      }
    }

    recommendations.sort((a, b) => a.orderByDate - b.orderByDate || a.sku.localeCompare(b.sku));

    return {
      recommendations,
      stockoutEvents,
      ranAt: settings.today,
    };
  }
}
